import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { toJS } from 'mobx';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import CalendarHourGrid from './hourGrid';
import CalendarHourGridMonth from './hourGridMonth';
import CalendarTodos from './calendarTodos';
import CsvEventImport from '../calendarEvent/csvImport';

import localUtil from '../../lib/util';
import { isNorwegianHoliday, getHolidayName } from '../../lib/norwegianHolidays';
import { getAvailabilityRange } from '../../lib/calendarAvailabilityRange';
import { canSendBulkSms } from '../../lib/smsBulk';
import { getCalendarColorIdx } from '../../lib/calendarColor';
// Access members at render time only (componentsOther.X) — this registry sits
// in an import cycle and is not fully evaluated at module scope.
import componentsOther from '../../product/components-other';

function formatDate(date) {
    return new Intl.DateTimeFormat('nb-NO', { year: 'numeric', month: 'long' }).format(date);
}

const dayNames = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
];
@observer
class CalendarInline extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            showCsvImport: false,
        };
        this.scrollAmount = 300;
        this.scrollContainer = null;
    }

    loadAll = async (props = this.props, skipDateReset) => {
        const { calendar = 'booking', startDate, endDate } = props;
        const { userStore, calendarStore, calendarEventStore } = this.props.stores;
        const { calendarDate, viewName } = calendarStore;
        const { customer = {} } = userStore;

        // Only set the date if skipDateReset is false AND a specific startDate was provided
        if (!skipDateReset && startDate !== undefined) {
            calendarStore.calendarSetDate(startDate);
        }
        // calendarStore.calendarSetDate(startDate);
        if (customer.calendarStartHour) {
            calendarStore.setStartHour(customer.calendarStartHour);
        }
        if (customer.calendarEndHour) {
            calendarStore.setEndHour(customer.calendarEndHour);
        }
        calendarStore.getHoursInDay();
        calendarStore.getDays();

        await calendarStore.load({
            query: {
                type: calendar,
            },
        });

        // const calendarId = calendarStore.calendars.filter(calendarStore.filterCalendars).map(c => c.calendarId);
        const calendarId = calendarStore.calendars.map(c => c.calendarId);
        const eventQuery = {
            calendarId,
            calendarDate: util.isoDate(calendarDate, false, false, true),
            viewName,
        };
        if (calendarStore.showCancelled) {
            eventQuery.showCancelled = 1;
        }
        if (this.includeConventions()) {
            eventQuery.includeConventions = 1;
        }
        await calendarEventStore.load({
            query: eventQuery,
            // The store default (limit 500, sort -createdDate) silently drops
            // bookings for busy clinics in month view - load the whole window.
            limit: 10000,
            sort: 'start',
            addData: ['recurringList', 'currentTime', 'currentTimeOffset', 'creators', 'animals', 'visitors'],
        });
        calendarEventStore.isOverlapping();
        this.loadCalendarAvailability(props);
    }

    // Conventions (stevner/treninger) are merged into the event list by the
    // backend when asked. Only ask when the C toggle exists (not in the
    // Dyrejournal build), applies to this customer, and is switched on.
    includeConventions = () => {
        const { calendarStore } = this.props.stores;
        const Toggle = componentsOther.ConventionCalendarToggle;
        return !!(Toggle && Toggle.isAvailable(this.props.stores) && calendarStore.showConventions);
    }

    loadCalendarEvents = async (props = this.props) => {
        const { calendarStore, calendarEventStore } = this.props.stores;
        const { calendarDate, viewName } = calendarStore;
        const calendarId = calendarStore.calendars.map(c => c.calendarId);
        const eventQuery = {
            calendarId,
            calendarDate: util.isoDate(calendarDate, false, false, true),
            viewName,
        };
        if (calendarStore.showCancelled) {
            eventQuery.showCancelled = 1;
        }
        if (this.includeConventions()) {
            eventQuery.includeConventions = 1;
        }
        await calendarEventStore.load({
            query: eventQuery,
            limit: 10000,
            sort: 'start',
            addData: ['recurringList', 'currentTime', 'currentTimeOffset', 'creators', 'animals', 'visitors'],
        });
        calendarEventStore.isOverlapping();
        this.loadCalendarAvailability(props);
    }

    // loadCalendarAvailability = async (props = this.props) => {
    //     const { calendarAvailabilityStore } = this.props.stores;
    //     await calendarAvailabilityStore.load({ query: {}});
    // }
    loadCalendarAvailability = async (props = this.props) => {
        const { calendarStore, calendarAvailabilityStore } = this.props.stores;
        // Week view shows the full ISO week of calendarDate, which can span a
        // month boundary - load the month padded to full weeks so every
        // displayed day has its availability rows (TICKET-01338).
        const { start, end } = getAvailabilityRange(calendarStore.calendarDate);
        await calendarAvailabilityStore.load({
            query: {
                start: util.isoDate(start, false, false, true),
                end: util.isoDate(end, false, false, true),
            },
        });
    }

    addEventCallback = async (event, calendarHtmlId) => {
        this.loadAll(this.props, true);
    }

    clickDayInMonth = (e, day) => {
        // Don't trigger if clicking on an event
        if (e.target.closest('.calendar-event')) {
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        const { calendarSelectDateTime, skipAnimal, enableSelection = true } = this.props;
        if (!enableSelection) {
            return;
        }

        const { appState, calendarStore, saleStore } = this.props.stores;
        const { calendars } = calendarStore;

        // Set start time to current time of day, rounded to nearest 15 minutes
        const now = new Date();
        const minutes = Math.round(now.getMinutes() / 15) * 15;
        const startDate = new Date(day);
        startDate.setHours(now.getHours(), minutes, 0, 0);

        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + 60); // Default 1 hour duration

        // Use the first visible calendar
        const visibleCalendars = calendars.filter(calendarStore.filterCalendars);
        const selectedCalendarId = visibleCalendars.length > 0 ? visibleCalendars[0].calendarId : null;

        if (!selectedCalendarId) {
            console.warn('No calendar selected');
            return;
        }

        if (calendarSelectDateTime) {
            calendarSelectDateTime({
                start: startDate,
                end: endDate,
                calendarId: selectedCalendarId,
            });
            return;
        }

        const { drawerLevel = 1, callback = () => {} } = this.props;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;
        const { animals } = newSale;

        appState.openDrawer('newCalendarEvent', {
            height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarType: 'booking',
            calendarId: selectedCalendarId,
            animals,
            startDate,
            endDate,
            skipAnimal,
            callback: async booking => {
                appState.toggleDrawer(false, drawerLevel + 1);
                if (callback) callback(booking);
                calendarStore.resetDragging();
                this.loadAll(this.props, true);
            },
        }, drawerLevel + 1);
    }

    clickHeader = e => {
        e.stopPropagation();
        e.preventDefault();
        const { calendarStore, appState, saleStore } = this.props.stores;
        const { calendarSelectDateTime, calendarId } = this.props;

        const { drawerLevel = 1, callback = () => {}, skipAnimal } = this.props;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('newCalendarAvailability', {
			height: drawerHeightLarge,
            callback: async () => {
                this.loadAll();
            },
		}, drawerLevel + 1);
    }

    handleScroll = e => {
        let isSticky = false;
        const div = e.target;
        if (div.scrollTop > 100) { // Adjust the threshold as needed
            isSticky = true;
        }
        this.setState({ isSticky });
    }

    onClickEditCalendar = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('div').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, customerStore } = this.props.stores;
        customerStore.load(id);
		const { drawerHeightMedium } = appState;
		appState.openDrawer('editCalendar', {
			height: drawerHeightMedium,
            id,
            callback: () => {
                this.loadAll();
            },
		}, drawerLevel + 1);
    }

    copyCalendarIcalLink = async (e, cal) => {
        e.preventDefault();
        e.stopPropagation();
        const apiServer = util.getApiServer();
        const url = `${apiServer}/api/calendarevents/ical/c/${cal.uuidv4}`;
        try {
            await navigator.clipboard.writeText(url);
        } catch (err) {
            // fallback: ignore
        }
        this.setState({ copiedCalendarId: cal.id });
        setTimeout(() => this.setState({ copiedCalendarId: null }), 2000);
    }



    updateScrollButtons = () => {
        const container = this.scrollContainer;
        if (container) {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            this.setState({
                canScrollLeft: scrollLeft > 0,
                canScrollRight: scrollLeft + clientWidth < scrollWidth,
            });
        }
    }

    scrollLeft = (e) => {
        e.stopPropagation();
        const container = this.scrollContainer;
        if (container) {
            container.scrollBy({ left: -this.scrollAmount, behavior: 'smooth' });
        }
    }

    scrollRight = (e) => {
        e.stopPropagation();
        const container = this.scrollContainer;
        if (container) {
            container.scrollBy({ left: this.scrollAmount, behavior: 'smooth' });
        }
    }

    handleCalendarDisplayChange = e => {
        const { calendarStore } = this.props.stores;
        const options = e.target.options;
        const selectedIds = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                selectedIds.push(parseInt(options[i].value, 10));
            }
        }
        // Update the observable array with selected IDs
        calendarStore.toggleCalendarDisplay(null, selectedIds);
    }

    toggleCalendar = id => {
        const { calendarStore } = this.props.stores;
        calendarStore.toggleCalendarDisplay(parseInt(id, 10));
    }

    showAllCalendars = () => {
        const { calendarStore } = this.props.stores;
        calendarStore.showAllCalendars();
    }

    // Calendars drawn as columns for ONE day: the "Velg kalendere" selection,
    // optionally narrowed by the toolbar toggle to calendars that have a turnus
    // row on that day. Week view calls this once per day column, so Monday can
    // show a different set than Tuesday. Returns a fresh array; the store's
    // calendars list is never touched.
    getVisibleCalendars = date => {
        const { calendarStore, calendarAvailabilityStore } = this.props.stores;
        const visible = calendarStore.calendars.filter(calendarStore.filterCalendars);
        if (!calendarStore.showOnlyActiveTurnus) {
            return visible;
        }
        return visible.filter(c => calendarAvailabilityStore.hasAvailability(date, c.id));
    }

    // Kalender-SMS (Dyrejournal): "SMS til dagens avtaler". Same product gate
    // as the Dyrejournal menu entries and the same admin gate as Utsendelse.
    canSendDaySms = () => {
        const { appState, userStore } = this.props.stores;
        const { isAdmin, isCustomerAdmin } = userStore;
        const { currentCustomer = {}, opts = {} } = appState;
        const isDyrejournal = !!(currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal);
        return isDyrejournal && canSendBulkSms({ isAdmin, isCustomerAdmin });
    }

    openDaySms = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { calendarDate, calendarIds } = e.currentTarget.dataset;
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        let ids = [];
        try {
            ids = JSON.parse(calendarIds || '[]');
        } catch (err) {
            ids = [];
        }
        appState.openDrawer('sendCalendarDaySms', {
            height: appState.drawerHeightLarge,
            calendarDate,
            calendarIds: Array.isArray(ids) ? ids : [],
        }, drawerLevel + 1);
    }

    componentDidMount() {
        this.loadAll();

        const container = this.scrollContainer;
        if (container) {
            container.addEventListener('scroll', this.updateScrollButtons);
            window.addEventListener('resize', this.updateScrollButtons);
            this.updateScrollButtons();
        }
    }

    componentWillUnmount() {
        const container = this.scrollContainer;
        if (container) {
            container.removeEventListener('scroll', this.updateScrollButtons);
            window.removeEventListener('resize', this.updateScrollButtons);
        }
    }

    // Todos are stored per-calendar (not per-date), so week/month views render one
    // "Gjøremål" box per visible calendar in a row ABOVE the grid, not repeated under
    // each day-column. When more than one calendar is visible we show a coloured name
    // pill above each box so it's clear which calendar the list belongs to.
    renderCalendarTodosRow = () => {
        const { calendarStore, userStore } = this.props.stores;
        const { customer } = userStore;
        // Gated per-clinic: only show the todo list above week/month views when the
        // customer has opted in via featureFlags.calendarTodosWeekMonth. Day view is
        // unaffected and always shows todos per-column.
        if (parseInt(customer?.featureFlags?.calendarTodosWeekMonth, 10) !== 1) {
            return null;
        }
        const { calendars, colors } = calendarStore;
        const visibleCalendars = calendars.filter(calendarStore.filterCalendars);
        if (!visibleCalendars.length) {
            return null;
        }
        const showCalendarName = visibleCalendars.length > 1;
        return (
            <div
                class='w-100 mt-2 mb-2 calendar-todos-row'
                style='display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); column-gap: 0.5rem; row-gap: 0.5rem;'
            >
                {visibleCalendars.map(c => {
                    const colorIdx = getCalendarColorIdx(calendars, c.id);
                    const color = c.color ? c.color : colors[colorIdx]?.color || '#000000';
                    const backgroundColor = c.backgroundColor ? c.backgroundColor : colors[colorIdx]?.backgroundColor || '#ffffff';
                    return (
                        <div key={`todos-${c.id}`} class='d-flex flex-column'>
                            {showCalendarName && (
                                <div
                                    class='rounded-pill px-2 py-1 text-center calendar-header'
                                    style={`background-color: ${backgroundColor}; color: ${color};`}
                                    data-id={c.id}
                                >
                                    <nobr><small>{c.name}</small></nobr>
                                </div>
                            )}
                            <CalendarTodos calendar={c} stores={this.props.stores} />
                        </div>
                    );
                })}
            </div>
        );
    };

    render() {
        const { isSticky, canScrollLeft = true, canScrollRight = true, copiedCalendarId } = this.state;
        const { calendarSelectDateTime, skipAnimal, inputKey, showPullDownHandle, pulldownHandleId, showViewEvent = true, enableSelection = true } = this.props;
        const { appState, userStore, calendarStore, calendarEventStore, calendarAvailabilityStore } = this.props.stores;
        const {
            colors,
            viewName = 'day',
            showMonth,
            calendarDate,
            calendarDateStart,
            calendarDateEnd,
            isToday,
            isTodayWeek,
            allDaysInWeek,
            allDaysInMonth,
            allWeeksInMonth,
        } = calendarStore;
        const { currentTime, currentTimeOffset } = calendarEventStore;
        const { calendars, displayCalendarIds, showOnlyActiveTurnus } = calendarStore;
        // Day-view columns. Week view resolves its own list per day column.
        const dayCalendars = this.getVisibleCalendars(calendarDate);
        const { user = {}, customer, isAdmin, isTester, isVeterinary } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const localTime = util.isoDate(new Date());
        const localTimeOffset = new Date().getTimezoneOffset();
        const timeIsDifferent = localTime !== currentTime;

        const calendarYYYY = calendarDate ? calendarDate.getFullYear() : '';
        const calendarMM = calendarDate ? calendarDate.getMonth() + 1 : '';
        const calendarDD = calendarDate ? calendarDate.getDate() : '';
        const calendarWeek = calendarDate ? util.getWeek(calendarDate) : '';

        // Calculate ISO week year (can differ from calendar year at year boundaries)
        let calendarWeekYear = calendarYYYY;
        if (calendarDate) {
            const month = calendarDate.getMonth(); // 0-11
            // If week 1 and month is December, week belongs to next year
            if (calendarWeek === 1 && month === 11) {
                calendarWeekYear = calendarYYYY + 1;
            // If week >= 52 and month is January, week belongs to previous year
            } else if (calendarWeek >= 52 && month === 0) {
                calendarWeekYear = calendarYYYY - 1;
            }
        }

        return (<>
            <div class='w-100 h-100 d-flex justify-content-center overflow-auto' style='isolation: isolate;' onScroll={this.handleScroll}>
                <div class='w-100 h-100'>
                    {/* Toolbar */}
                    <div class='d-flex flex-row w-100 align-items-center justify-content-between px-3 py-2 border-bottom flex-wrap gap-2'>
                        {/* Left: Navigation + Date label */}
                        <div class='d-flex flex-nowrap align-items-center'>
                            <button class='btn btn-sm btn-outline-primary rounded-pill-left' onClick={() => calendarStore.calendarPrev(this.loadCalendarEvents)}>
                                <i class='fa-duotone fa-caret-left' /> <Text id='calendar.prev'>Prev</Text>
                            </button>
                            <button class={`btn btn-sm btn-${isToday ? 'primary' : 'outline-primary'} rounded-none`} onClick={() => calendarStore.calendarToday(this.loadCalendarEvents)}>
                                <Text id='calendar.today'>Today</Text>
                            </button>
                            <button class='btn btn-sm btn-outline-primary rounded-pill-right' onClick={() => calendarStore.calendarNext(this.loadCalendarEvents)}>
                                <Text id='calendar.next'>Next</Text> <i class='fa-duotone fa-caret-right' />
                            </button>
                            {calendarDate && <span class='ms-3 text-body-secondary fw-light' style='font-size: 1.1em;'>
                                {viewName === 'month' && <span>{calendarYYYY}-{calendarMM}</span>}
                                {viewName === 'week' && <span>{calendarWeekYear} w{calendarWeek}</span>}
                                {viewName === 'day' && <>
                                    <span>
                                        {util.formatDate(calendarDate, {
                                            weekday: 'short',
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            locale: 'nb-NO',
                                        })},
                                        <span class='ms-1'>
                                            {calendarWeekYear !== calendarYYYY
                                                ? `(${calendarWeekYear} w${calendarWeek})`
                                                : `w${calendarWeek}`
                                            }
                                        </span>
                                    </span>
                                    {getHolidayName(calendarDate) && <span class='ms-2 badge bg-danger'>{getHolidayName(calendarDate)}</span>}
                                </>}
                            </span>}
                            {viewName === 'day' && calendarDate && this.canSendDaySms() && <Localizer>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-primary rounded-pill ms-3 text-nowrap'
                                    title={<Text id='dyrejournal.calendar-sms-button'>SMS to the day's appointments</Text>}
                                    data-calendar-date={util.isoDate(calendarDate, false, false, true)}
                                    data-calendar-ids={JSON.stringify(dayCalendars.map(c => c.calendarId))}
                                    onClick={this.openDaySms}
                                >
                                    <i class='fa-duotone fa-comment-sms me-1' />
                                    <Text id='dyrejournal.calendar-sms-button'>SMS to the day's appointments</Text>
                                </button>
                            </Localizer>}
                        </div>

                        {/* Center: Calendar selector */}
                        <div class='d-none d-md-flex align-items-center'>
                            <details style='position: relative;'>
                                <summary>
                                    <span class='btn btn-sm btn-outline-secondary rounded-pill'>
                                        <i class='fa-duotone fa-calendars me-1' />
                                        <Text id='calendar.select-calendars-display'>Calendars</Text>
                                        {displayCalendarIds.length > 0 && <span class='badge bg-danger ms-1'>{displayCalendarIds.length}/{calendars.length}</span>}
                                    </span>
                                </summary>
                                <div class='position-absolute list-group shadow bg-body' style='z-index: 1050; min-width: 220px;'>
                                    {calendars.map(calendar => (
                                        <label key={calendar.id} class='list-group-item list-group-item-action d-flex align-items-center py-1'>
                                            <input
                                                class='me-2'
                                                type='checkbox'
                                                checked={displayCalendarIds.includes(calendar.id)}
                                                onChange={() => this.toggleCalendar(calendar.id)}
                                            />
                                            {calendar.name}
                                            {calendar.isPublic && <i class='fa-solid fa-globe text-info ms-2' title='Public' />}
                                            {calendar.isMain && <i class='fa-solid fa-star text-warning ms-1' title='Main' />}
                                        </label>
                                    ))}
                                    {displayCalendarIds.length > 0 && <button class='list-group-item list-group-item-action text-center text-primary py-1' onClick={this.showAllCalendars}>
                                        <Text id='calendar.show-all-calendars'>Show all calendars</Text>
                                    </button>}
                                </div>
                            </details>
                        </div>

                        {/* Right: View toggles + gear */}
                        <div class='d-flex flex-nowrap align-items-center gap-1'>
                            <button type='button' class={`btn btn-sm btn-${viewName === 'month' ? 'primary' : 'outline-primary'} rounded-pill-left`} onClick={() => { calendarStore.calendarChangeView('month'); this.loadCalendarEvents(); }}>
                                <Text id='calendar.month'>month</Text>
                            </button>
                            <button type='button' class={`btn btn-sm btn-${viewName === 'week' ? 'primary' : 'outline-primary'} rounded-none`} onClick={() => { calendarStore.calendarChangeView('week'); this.loadCalendarEvents(); }}>
                                <Text id='calendar.week'>week</Text>
                            </button>
                            <button type='button' class={`btn btn-sm btn-${viewName === 'day' ? 'primary' : 'outline-primary'} rounded-pill-right`} onClick={() => { calendarStore.calendarChangeView('day'); this.loadCalendarEvents(); }}>
                                <Text id='calendar.day'>day</Text>
                            </button>
                            {viewName === 'week' && <>
                                <button type='button' class={`btn btn-sm btn-${calendarStore.workweek ? 'info' : 'outline-info'} rounded-pill ms-2`} onClick={() => { calendarStore.toggleWorkweek(); }}>
                                    {calendarStore.workweek ? '5 dager' : '7 dager'}
                                </button>
                                <button type='button' class={`btn btn-sm btn-${calendarStore.compact ? 'warning' : 'outline-warning'} rounded-pill`} onClick={() => { calendarStore.toggleCompact(); }}>
                                    {calendarStore.compact ? <i class='fa-duotone fa-compress' /> : <i class='fa-duotone fa-expand' />}
                                </button>
                            </>}
                            <button
                                type='button'
                                class={`btn btn-sm btn-${showMonth ? 'primary' : 'outline-primary'} rounded-pill ms-2`}
                                onClick={() => calendarStore.toggleShowMonth()}
                                title={showMonth ? 'Hide month' : 'Show month'}
                            >
                                <i class='fa-duotone fa-calendar-days' />
                            </button>
                            {viewName !== 'month' && <Localizer>
                                <button
                                    type='button'
                                    class={`btn btn-sm btn-${showOnlyActiveTurnus ? 'success' : 'outline-success'} rounded-pill`}
                                    onClick={() => calendarStore.toggleShowOnlyActiveTurnus()}
                                    title={showOnlyActiveTurnus
                                        ? <Text id='calendar.show-all-calendars-turnus'>Turnus filter is on. Show all calendars</Text>
                                        : <Text id='calendar.show-only-active-turnus'>Show only calendars with turnus today</Text>}
                                >
                                    <i class='fa-duotone fa-user-clock' />
                                </button>
                            </Localizer>}
                            <button
                                type='button'
                                class={`btn btn-sm btn-${calendarStore.showCancelled ? 'danger' : 'outline-danger'} rounded-pill`}
                                onClick={() => { calendarStore.toggleShowCancelled(); this.loadCalendarEvents(); }}
                                title={calendarStore.showCancelled ? 'Hide cancelled bookings' : 'Show cancelled bookings'}
                            >
                                <i class='fa-duotone fa-calendar-xmark' />
                            </button>
                            {componentsOther.ConventionCalendarToggle && <componentsOther.ConventionCalendarToggle stores={this.props.stores} onToggle={this.loadCalendarEvents} />}
                            <button
                                type='button'
                                class={`btn btn-sm btn-${this.state.showCsvImport ? 'info' : 'outline-info'} rounded-pill`}
                                onClick={() => this.setState(prev => ({ showCsvImport: !prev.showCsvImport }))}
                                title='Import events from CSV'
                            >
                                <i class='fa-solid fa-file-import' />
                            </button>
                            <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.clickHeader} title='Settings'>
                                <i class='fa-regular fa-gear' />
                            </button>
                        </div>
                    </div>

                    {this.state.showCsvImport && (
                        <CsvEventImport
                            stores={this.props.stores}
                            onClose={() => this.setState({ showCsvImport: false })}
                            onComplete={() => this.loadCalendarEvents()}
                        />
                    )}

                    {showMonth ? <>
                        <div class='d-flex flex-row w-100 justify-content-center'>
                        <div class='d-flex flex-column w-100 mt-3 px-3' style='max-width: 500px;'>

                            <div class='d-flex flex-row flex-nowrap'>
                                <button class={`btn btn-sm btn-primary rounded-pill-left`} onClick={() => calendarStore.calendarPrevMonth(this.loadCalendarEvents)}>
                                    <i class='fa-duotone fa-caret-left' />
                                </button>
                                <button class={`flex-fill btn btn-sm btn-outline-primary rounded-none`} disabled>
                                    {formatDate(calendarDate)}
                                </button>
                                <button class={`btn btn-sm btn-primary rounded-pill-right`} onClick={() => calendarStore.calendarNextMonth(this.loadCalendarEvents)}>
                                    <i class='fa-duotone fa-caret-right' />
                                </button>
                            </div>

                            <div class='table-responsive mt-1'>
                                <table class='table table-sm table-bordered table-hover'>
                                    <thead>
                                        <tr>
                                            <th class='text-center p-0'><Text id='calendar.week'>Week</Text></th>
                                            <th class='text-center p-0'><Text id='calendar.monday-short'>M</Text></th>
                                            <th class='text-center p-0'><Text id='calendar.tuesday-short'>T</Text></th>
                                            <th class='text-center p-0'><Text id='calendar.wednesday-short'>W</Text></th>
                                            <th class='text-center p-0'><Text id='calendar.thursday-short'>T</Text></th>
                                            <th class='text-center p-0'><Text id='calendar.friday-short'>F</Text></th>
                                            <th class='text-center p-0'><Text id='calendar.saturday-short'>S</Text></th>
                                            <th class='text-center p-0'><Text id='calendar.sunday-short'>S</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {calendarStore.weeksInMonth().map((obj, idx) => {
                                            if (!obj.week) {
                                                return '';
                                            }
                                            return (
                                                <tr key={`week-${obj.week?.week}-${idx}`}>
                                                    <td class='text-center font-weight-lighter p-0'>
                                                        {obj.week?.week}
                                                    </td>
                                                    {obj.week?.daysInWeek.map((day, dayIdx) => {
                                                        const dayOfWeek = day.getDay();
                                                        const isToday = day.toDateString() === new Date().toDateString();
                                                        const currentIsToday = calendarDate.toDateString() === new Date().toDateString();
                                                        const isCurrentDay = day.toDateString() === calendarDate.toDateString() && !currentIsToday;
                                                        const miniHolidayName = getHolidayName(day);
                                                        return (
                                                            <td
                                                                key={`day-${dayIdx}`}
                                                                class={`text-center p-0
                                                                    ${(dayOfWeek === 6 || dayOfWeek === 0 || miniHolidayName) ? 'text-danger' : ''}
                                                                    ${isToday ? 'font-weight-bolder' : ''}
                                                                    ${isCurrentDay ? 'bg-primary text-white' : ''}
                                                                `}
                                                                style={`
                                                                    ${isToday ? 'font-weight: 1000 !important;' : ''}
                                                                    ${miniHolidayName ? 'font-weight: bold;' : ''}
                                                                `}
                                                                title={miniHolidayName || ''}
                                                                onClick={() => calendarStore.calendarSetDate(day, this.loadCalendarEvents)}
                                                            >
                                                                {day.getDate()}
                                                            </td>);
                                                    })}
                                                </tr>);
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        </div>
                    </> : <></>}


                    {viewName === 'day' && <>
                        {showOnlyActiveTurnus && dayCalendars.length === 0 && (
                            <div class='w-100 mt-4 mb-5 text-center text-body-secondary'>
                                <div><i class='fa-duotone fa-user-clock fa-2x mb-2' /></div>
                                <Text id='calendar.no-active-turnus'>No calendars have turnus on this day.</Text>
                                <div class='mt-2'>
                                    <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={() => calendarStore.toggleShowOnlyActiveTurnus()}>
                                        <Text id='calendar.show-all-calendars'>Show all calendars</Text>
                                    </button>
                                </div>
                            </div>
                        )}
                        {/*
                            Day-view columns are laid out as a CSS grid with three row tracks
                            (header / todos / hour-grid). Each calendar column is a subgrid spanning
                            those rows, so the browser sizes each track to its tallest cell across ALL
                            columns. That keeps every column's "Gjøremål" box the same height and the
                            hour grids (and the red "now" line) aligned, even when only one column has
                            todos. See docs/superpowers/specs/2026-06-09-calendar-todo-improvements-design.md

                            Column sizing MUST use `minmax(min-content, 1fr)`, NOT `minmax(0, 1fr)`:
                            the hour grid has a hard `min-width: 215px` per row (hourGrid.js), so a 0
                            minimum would crush every column below its content and kill horizontal
                            scroll — breaking clinics with many calendars (TICKET-01010). `min-content`
                            restores the old flexbox `min-width: auto` behaviour: fill the width when
                            there's room, fall back to content width + horizontal scroll when there are
                            many columns.
                        */}
                        <div
                            class='w-100 mt-2 mb-5 overflow-auto calendar-day-grid'
                            style={`
                                display: grid;
                                grid-auto-flow: column;
                                grid-auto-columns: minmax(min-content, 1fr);
                                grid-template-rows: auto auto auto;
                                column-gap: 0.5rem;
                            `}
                            onTouchmove={localUtil.captureEvents}
                        >
                            {dayCalendars.map((c, idx) => {
                                // Colour follows the calendar, not the column it happens to sit in.
                                const colorIdx = getCalendarColorIdx(calendars, c.id);
                                const color = c.color ? c.color : colors[colorIdx]?.color || '#000000';
                                const backgroundColor = c.backgroundColor ? c.backgroundColor : colors[colorIdx]?.backgroundColor || '#ffffff';
                                const { startHour, startMinute, endHour, endMinute, isOnlineBooking, hasOnlineBooking, onlineStartHour, onlineStartMinute, onlineEndHour, onlineEndMinute } = calendarAvailabilityStore.getAvailableHoursWithPriority(calendarDate, c.id);

                                return (
                                    <div
                                        key={`cal-${c.id}`}
                                        class='px-1'
                                        style='grid-row: 1 / -1; display: grid; grid-template-rows: subgrid;'
                                    >
                                        <div
                                            class='rounded-pill my-1 px-2 py-1 flex-fill text-center calendar-header'
                                            style={`
                                                background-color: ${backgroundColor};
                                                color: ${color};
                                            `}
                                            data-id={c.id}
                                        >
                                            <nobr>
                                                <small>
                                                    {c.name}
                                                    {c.isPublic && <i class='fa-solid fa-globe ms-2' title='Public' />}
                                                    {c.isMain && <i class='fa-solid fa-star ms-1' title='Main' />}
                                                    {startHour && endHour && <>
                                                        <span class='ms-2 font-weight-lighter'>({util.padDate(startHour)}:{util.padDate(startMinute)}-{util.padDate(endHour)}:{util.padDate(endMinute)})</span>
                                                    </>}
                                                    <i class='fa-duotone fa-edit ms-2' onClick={this.onClickEditCalendar} />
                                                    {c.uuidv4 && (copiedCalendarId === c.id
                                                        ? <i class='fa-solid fa-check ms-1 text-success' title='Copied!' />
                                                        : <i class='fa-solid fa-calendar-plus ms-1' onClick={(e) => this.copyCalendarIcalLink(e, c)} title='Copy iCal link' />
                                                    )}
                                                </small>
                                            </nobr>
                                        </div>
                                        <CalendarTodos calendar={c} stores={this.props.stores} />
                                        <div class={`w-100 px-2 py-1 mt-2  ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                                            <CalendarHourGrid
                                                key={`calendar-${c.id}-${inputKey}`}
                                                stores={this.props.stores}
                                                idx={idx}
                                                colorIdx={colorIdx}
                                                calendarDate={calendarDate}
                                                calendarId={c.calendarId}
                                                calendarSelectDateTime={calendarSelectDateTime}
                                                callback={this.addEventCallback}
                                                skipAnimal={skipAnimal}
                                                displayCustomerInfo={c.displayCustomerInfo}

                                                useCalendarAvailability={c.useCalendarAvailability}
                                                availabilityHoursStart={startHour}
                                                availabilityMinutesStart={startMinute}
                                                availabilityHoursEnd={endHour}
                                                availabilityMinutesEnd={endMinute}
                                                isOnlineBookingAvailability={isOnlineBooking}
                                                hasOnlineBooking={hasOnlineBooking}
                                                onlineBookingHoursStart={onlineStartHour}
                                                onlineBookingMinutesStart={onlineStartMinute}
                                                onlineBookingHoursEnd={onlineEndHour}
                                                onlineBookingMinutesEnd={onlineEndMinute}
                                                showPullDownHandle={showPullDownHandle}
                                                pulldownHandleId={pulldownHandleId}
                                                showViewEvent={showViewEvent}
                                                enableSelection={enableSelection}
                                                color={color}
                                                backgroundColor={backgroundColor}
                                            />
                                        </div>
                                    </div>);
                            })}
                        </div>
                    </>}

                    {viewName === 'week' && <>
                        {this.renderCalendarTodosRow()}
                        <div
                            class='scroll-wrapper'
                            onTouchmove={localUtil.captureEvents}
                        >
                            {canScrollLeft && (
                                <button
                                    className="scroll-button left rounded-pill"
                                    onClick={this.scrollLeft}
                                    aria-label="Scroll Left"
                                >
                                    ◀
                                </button>
                            )}
                            {canScrollRight && (
                                <button
                                    className="scroll-button right rounded-pill"
                                    onClick={this.scrollRight}
                                    aria-label="Scroll Right"
                                >
                                    ▶
                                </button>
                            )}
                            <div
                                class={`d-flex flex-row w-100 mt-0 mb-5 ${calendarStore.compact ? 'overflow-hidden' : 'overflow-auto'}`}
                                ref={c => this.scrollContainer = c}
                            >
                                {/* Left time column */}
                                <div class='d-flex flex-column flex-shrink-0' style='width: 40px;'>
                                    <div style='height: 30px;'></div>
                                    <div style='height: 24px;'></div>
                                    {calendarStore.calendarHours.map(hr => {
                                        const { featureFlags: ff = {} } = customer || {};
                                        const hourHeight = parseInt(ff.calendarHourHeight, 10) || 80;
                                        return (
                                            <div key={`tl-${hr.getHours()}`} class='d-flex flex-row justify-content-end pe-1' style={`height: ${hourHeight}px;`}>
                                                <small class='text-muted font-weight-light font-style-narrow'>{util.isoTime(hr)}</small>
                                            </div>
                                        );
                                    })}
                                </div>

                                {allDaysInWeek.map((day, idx) => {
                                    const dayOfWeek = day.getUTCDay();
                                    const dayName = dayNames[dayOfWeek];
                                    if (calendarStore.workweek && (dayOfWeek === 0 || dayOfWeek === 6)) {
                                        return '';
                                    }
                                    if (customer.calendarDays) {
                                        if (!customer.calendarDays[dayName]) {
                                            return '';
                                        }
                                    }
                                    const holidayName = getHolidayName(day);
                                    const isCompact = calendarStore.compact;
                                    const weekDayCalendars = this.getVisibleCalendars(day);
                                    return (
                                        <div key={`wd-${idx}`} class={`d-flex flex-column h-100 mt-0 px-0 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`} style={isCompact ? 'min-width: 0; flex: 1 1 0%; margin: 0 2px;' : 'margin: 0 2px;'}>
                                            <div class={`d-flex flex-column w-100 align-items-center ${dayOfWeek === 6 || dayOfWeek === 0 || holidayName ? 'bg-danger' : 'bg-secondary'} text-white rounded-top-lg py-1 ${isCompact ? 'text-truncate' : ''}`} style={isCompact ? 'min-width: 0;' : ''}>
                                                <div class={`${isCompact ? 'text-truncate' : ''} w-100 text-center`} style='font-size: 0.85em;'>
                                                    {util.formatDate(day, { weekday: isCompact ? 'short' : 'long', day: 'numeric', month: 'short', year: isCompact ? undefined : 'numeric', locale: 'nb-NO' })}
                                                    {this.canSendDaySms() && <Localizer>
                                                        <i
                                                            class='fa-duotone fa-comment-sms ms-2'
                                                            role='button'
                                                            title={<Text id='dyrejournal.calendar-sms-button'>SMS to the day's appointments</Text>}
                                                            data-calendar-date={util.isoDate(day, false, false, true)}
                                                            data-calendar-ids={JSON.stringify(weekDayCalendars.map(c => c.calendarId))}
                                                            onClick={this.openDaySms}
                                                        />
                                                    </Localizer>}
                                                </div>
                                                {holidayName && <div class='small fw-light text-truncate w-100 text-center'>{holidayName}</div>}
                                            </div>
                                            <div class='d-flex flex-row w-100' style='min-width: 0;'>
                                                {showOnlyActiveTurnus && weekDayCalendars.length === 0 && (
                                                    <div
                                                        class='d-flex flex-column align-items-center justify-content-center w-100 px-2 py-3 text-body-secondary small text-truncate'
                                                        style={isCompact ? 'min-width: 0;' : 'min-width: 120px;'}
                                                    >
                                                        <i class='fa-duotone fa-user-clock mb-1' />
                                                        <Text id='calendar.no-active-turnus-short'>No turnus</Text>
                                                    </div>
                                                )}
                                                {weekDayCalendars.map((c, cidx) => {
                                                    const colorIdx = getCalendarColorIdx(calendars, c.id);
                                                    const color = c.color ? c.color : colors[colorIdx]?.color || '#000000';
                                                    const backgroundColor = c.backgroundColor ? c.backgroundColor : colors[colorIdx]?.backgroundColor || '#ffffff';
                                                    const { startHour, startMinute, endHour, endMinute, isOnlineBooking, hasOnlineBooking, onlineStartHour, onlineStartMinute, onlineEndHour, onlineEndMinute } = calendarAvailabilityStore.getAvailableHoursWithPriority(day, c.id);

                                                    return (
                                                        <div key={`wc-${c.id}`} class={`d-flex flex-column ${isCompact ? '' : 'w-100'} h-100 mt-0 mx-0 px-0 overflow-hidden ${cidx > 0 ? 'border-start' : ''}`} style={`${isCompact ? 'min-width: 0; flex: 1 1 0%;' : ''} ${cidx > 0 ? 'border-color: var(--bs-border-color) !important;' : ''}`}>
                                                            <div
                                                                class='my-0 px-1 py-1 text-center calendar-header text-truncate'
                                                                style={`
                                                                    background-color: ${backgroundColor};
                                                                    color: ${color};
                                                                `}
                                                                data-id={c.id}
                                                            >
                                                                <small class='text-truncate'>
                                                                    {c.name}
                                                                    {!isCompact && c.isPublic && <i class='fa-solid fa-globe ms-1' title='Public' />}
                                                                    {!isCompact && c.isMain && <i class='fa-solid fa-star ms-1' title='Main' />}
                                                                    {!isCompact && <i class='fa-duotone fa-edit ms-1' onClick={this.onClickEditCalendar} />}
                                                                    {!isCompact && c.uuidv4 && (copiedCalendarId === c.id
                                                                        ? <i class='fa-solid fa-check ms-1 text-success' title='Copied!' />
                                                                        : <i class='fa-solid fa-calendar-plus ms-1' onClick={(e) => this.copyCalendarIcalLink(e, c)} title='Copy iCal link' />
                                                                    )}
                                                                </small>
                                                            </div>
                                                            <div class='px-0 py-0 mt-0 overflow-hidden' style='min-width: 0;'>
                                                                <CalendarHourGrid
                                                                    key={`calendar-${c.id}-${inputKey}`}
                                                                    stores={this.props.stores}
                                                                    idx={cidx}
                                                                    colorIdx={colorIdx}
                                                                    calendarDate={day}
                                                                    calendarId={c.calendarId}
                                                                    calendarSelectDateTime={calendarSelectDateTime}
                                                                    callback={this.addEventCallback}
                                                                    skipAnimal={skipAnimal}
                                                                    displayCustomerInfo={c.displayCustomerInfo}

                                                                    useCalendarAvailability={c.useCalendarAvailability}
                                                                    availabilityHoursStart={startHour}
                                                                    availabilityMinutesStart={startMinute}
                                                                    availabilityHoursEnd={endHour}
                                                                    availabilityMinutesEnd={endMinute}
                                                                    isOnlineBookingAvailability={isOnlineBooking}
                                                                    hasOnlineBooking={hasOnlineBooking}
                                                                    onlineBookingHoursStart={onlineStartHour}
                                                                    onlineBookingMinutesStart={onlineStartMinute}
                                                                    onlineBookingHoursEnd={onlineEndHour}
                                                                    onlineBookingMinutesEnd={onlineEndMinute}
                                                                    showPullDownHandle={showPullDownHandle}
                                                                    pulldownHandleId={pulldownHandleId}
                                                                    showViewEvent={showViewEvent}
                                                                    enableSelection={enableSelection}
                                                                    color={color}
                                                                    backgroundColor={backgroundColor}
                                                                    hideTimeColumn={true}
                                                                    showTimeBubble={cidx === 0}
                                                                    minWidth={calendarStore.compact ? 0 : 120}
                                                                />
                                                            </div>
                                                        </div>);
                                                })}
                                            </div>
                                        </div>)
                                })}

                                {/* Right time column */}
                                <div class='d-flex flex-column flex-shrink-0' style='width: 40px;'>
                                    <div style='height: 30px;'></div>
                                    <div style='height: 24px;'></div>
                                    {calendarStore.calendarHours.map(hr => (
                                        <div key={`tr-${hr.getHours()}`} class='d-flex flex-row justify-content-start ps-1' style='height: 80px;'>
                                            <small class='text-muted font-weight-light font-style-narrow'>{util.isoTime(hr)}</small>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>}

                    {viewName === 'month' && <>
                        {this.renderCalendarTodosRow()}
                        <div
                            class='d-flex flex-column w-100 justify-content-between mt-0 mb-5 overflow-auto'
                            onTouchmove={localUtil.captureEvents}
                        >
                            <div class={`d-flex flex-row justify-content-between mt-1 px-0 rounded-lg`}>
                                {[1,2,3,4,5,6,0].map((dayIdx) => {
                                    return (
                                        <div key={`dn-${dayIdx}`} class={`d-flex flex-column align-items-end px-2 py-1 mx-1 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`} style='min-width: 13.9%;'>
                                            <span class='text-muted'>{calendarStore.getDayNameNo(dayIdx)}</span>
                                        </div>);
                                })}
                            </div>
                            {allWeeksInMonth.map((obj, wkIdx) => {
                                const { week = {} } = obj;
                                const { daysInWeek = [] } = week;
                                return (
                                    <div key={`mw-${wkIdx}`} class={`d-flex flex-row justify-content-between mt-1 px-0 rounded-lg`}>

                                        {daysInWeek && daysInWeek.map((day, dIdx) => {
                                            const date = day.getDate();
                                            const dow = day.getDay();
                                            const month = day.getMonth() + 1;
                                            const holidayName = getHolidayName(day);
                                            if (month !== calendarMM) {
                                                return (
                                                    <div key={`md-${dIdx}`} class={`d-flex flex-column px-1 mx-1`} style='min-width: 13.9%; min-height: 12vh;'>
                                                    </div>);
                                            }
                                            return (
                                                <div
                                                    key={`md-${dIdx}`}
                                                    class={`d-flex flex-column px-2 py-1 mx-1 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg ${holidayName ? 'border border-danger' : ''}`}
                                                    style='min-width: 13.9%; min-height: 12vh; cursor: pointer;'
                                                    onClick={e => this.clickDayInMonth(e, day)}
                                                >
                                                    <div class='d-flex flex-row justify-content-between'>
                                                        <div class='text-muted font-weight-lighter' style='font-size: 1.2em;'>{dow === 1 ? week.week : ''}</div>
                                                        <div class={`font-weight-lighter ${holidayName ? 'text-danger fw-bold' : 'text-muted'}`} style='font-size: 1.2em;'>{date}</div>
                                                    </div>
                                                    {holidayName && <div class='text-danger small text-truncate' title={holidayName}>{holidayName}</div>}
                                                    <div class='d-flex flex-column rounded'>
                                                        <CalendarHourGridMonth
                                                            key={`calendar-${inputKey}`}
                                                            stores={this.props.stores}
                                                            idx={1}
                                                            calendarDate={day}
                                                            calendarId={calendars.filter(calendarStore.filterCalendars).map(c => c.calendarId)}
                                                            calendarSelectDateTime={calendarSelectDateTime}
                                                            callback={this.addEventCallback}
                                                            skipAnimal={skipAnimal}

                                                            // useCalendarAvailability={c.useCalendarAvailability}
                                                            // availabilityHoursStart={startHour}
                                                            // availabilityMinutesStart={startMinute}
                                                            // availabilityHoursEnd={endHour}
                                                            // availabilityMinutesEnd={endMinute}
                                                            showPullDownHandle={showPullDownHandle}
                                                            pulldownHandleId={pulldownHandleId}
                                                            showViewEvent={showViewEvent}
                                                            enableSelection={enableSelection}
                                                            // color={color}
                                                            // backgroundColor={backgroundColor}
                                                        />
                                                    </div>
                                                </div>);
                                        })}
                                    </div>);
                            })}
                        </div>



                    </>}

                </div>
            </div>
        </>);
    }
}

export default CalendarInline;
